import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FormContainer from "../../../components/FormContainer";
import Estimasi from '../../../models/Estimasi';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { buttonTheme, PageContainerActionParamType, pageToolbarDetailHeight } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import TableView from '../../../components/TableView';
import EstimasiBrg from '../../../models/EstimasiBrg';
import {
    Add as AddIcon,
    FileDownload as FileDownloadIcon,
	FileUpload as FileUploadIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import FormModal from '../../../components/FormModal';
import Barang from '../../../models/Barang';
import { Box, Button, ButtonGroup, createTheme, SvgIconOwnProps, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@emotion/react';
import { ButtonFormTheme, GridColorStyles } from '../../../expanded-theme';
import { v4 as uuidv4, validate as uuidValidate } from "uuid";
import NotificationDialog from '../../../components/NotificationDialog';
import GridViewEdit from '../../../components/GridViewEdit';
import { deepmerge } from '@mui/utils';

const groupName: MenuGroupName = "Servis";
const subgroupName: string = "Master";
const code: MenuCode = "SERVICE-ESTIMASI";
const title: MenuTitle = "Estimasi";
const endpoint = "estimasi";
    
const gridIconStyle : SvgIconOwnProps = {
    fontSize: "small",
    sx: {marginRight: "0.1rem", paddingBottom: 0},
};

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<Estimasi>({ kode: '', nama: '', list_estimasi_brg: [], aktif: true });
    const [detailBrgToEdit, setDetailBrgToEdit] = useState<EstimasiBrg | null>(null);
    const [validationResult, setValidationResult] = useState<any[] | null>(null);
    const navigate = useNavigate();
    const brgRef = useRef();
    
    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => setFormData({...response.data?.data}))
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onDetailChanged = useCallback((detailData?: EstimasiBrg) => {
        if (!detailData)
            return;

        // console.log({detailData});
        const { list_estimasi_brg } = formData;

        if (detailData.brg)
        {
            detailData.kode_brg = detailData.brg.kode;
            detailData.nama_brg = detailData.brg.nama;
            delete detailData.brg;
        }

        if (!apiCaller)
            return;

        setLoading(true);

        apiCaller.get(`brg/id?ucode=${detailData.ucode_brg}`)
            .then((response) => {
                detailData.kode_brg = response.data?.data?.kode;
                detailData.nama_brg = response.data?.data?.nama;

                if (!detailData.ucode)
                {
                    detailData.ucode = uuidv4();
                    list_estimasi_brg.push(detailData);
                }
                else {
                    const currentDetail = list_estimasi_brg.filter(d => d.ucode === detailData.ucode)[0];
                    const currentIndex = list_estimasi_brg.indexOf(currentDetail);
                    list_estimasi_brg.splice(currentIndex, 1, detailData);
                }
        
                const newData = {
                    ...formData,
                    list_estimasi_brg
                };
        
                setFormData(JSON.parse(JSON.stringify(newData)));
            })
            .catch((e) => console.error({e}))
            .finally(() => setLoading(false))
        ;
    }, [formData, apiCaller]);

    const onValidate = useCallback((data: Estimasi | undefined) => {
        if (!data?.nama)
            return false;

        if (data?.ucode && !data.kode)
            return false;

        return true;
    }, []);

    const onValidateDetailBrg = useCallback((detailToCheck: EstimasiBrg, callback: Function) => {
        const existingData = formData.list_estimasi_brg.filter(d => d.ucode_brg === detailToCheck.ucode_brg && (detailToCheck.is_new || d.ucode !== detailToCheck.ucode));

        if (existingData.length)
        {
            setValidationResult([
                {
                    type: "Error",
                    message: "Data barang tidak boleh kembar.",
                }
            ]);

            return;
        }

        callback(detailToCheck);
    }, [formData]);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Estimasi> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const apiCallerObj = param.data.ucode ? apiCaller.put(`${endpoint}`, JSON.stringify(param.data)) : apiCaller.post(`${endpoint}`, JSON.stringify(param.data));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<Estimasi>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
					isViewMode={isViewMode}
                    fields={[
                        {
                            name: "kode",
                            title: "Kode",
                            inputType: "text",
                            dataType: (typeof formData.kode),
                            isReadonly: false,
                            isRequired: formData?.ucode ? true : false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Estimasi>) => onChanged(valueObj),
                        },
                        {
                            name: "nama",
                            title: "Nama",
                            inputType: "text",
                            dataType: (typeof formData.nama),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Estimasi>) => onChanged(valueObj),
                        },
                        {
                            name: "keterangan",
                            title: "Keterangan",
                            inputType: "textarea",
                            dataType: (typeof formData.keterangan),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Estimasi>) => onChanged(valueObj),
                            rows: 3,
                        }
                    ]}
                    onValidate={onValidate}
                    onClose={(pathObject) => onClose(pathObject)}
                    onSave={(data) => onSave(data)}
                />
                <Box
                    sx={{
                        marginTop: "1.5rem",
                        marginBottom: "0.5rem",
                        width: "100%",
                        height: `${pageToolbarDetailHeight}px`,
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "start",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            textAlign: "start",
                            borderBottom: "2px solid #D9E7E5",
                            color: "#185E91",
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography variant="button" sx={{fontSize: "1rem"}}>Detail Barang</Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        <ThemeProvider theme={buttonTheme}>
                            <ButtonGroup>
                                <Tooltip
                                    title="Tambah Detail Barang"
                                    arrow
                                >
                                    <Button variant="contained" color="primary" size="small" onClick={() => setDetailBrgToEdit({ is_new: true, qty: 0 })}>
                                        <AddIcon {...gridIconStyle} />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </ThemeProvider>
                    </Box>
                </Box>
                <GridViewEdit<EstimasiBrg>
                    id_field="ucode"
                    columns={[
                        {
                            columnName: "action",
                            columnTitle: "",
                            columnType: "action",
                            actions: [
                                {
                                    title: "Ubah",
                                    color: "primary",
                                    action: (rowData: any) => setDetailBrgToEdit({
                                        ...rowData,
                                        brg: {
                                            ucode: rowData.ucode_brg,
                                            kode: rowData.kode_brg,
                                            nama: rowData.nama_brg,
                                        }
                                    }),
                                    icon: EditIcon,
                                    variant: "outlined",
                                },
                                {
                                    title: "Hapus",
                                    color: "delete",
                                    action: (id: any) => {},
                                    icon: DeleteIcon,
                                    variant: "outlined",
                                }
                            ],
                            width: "10%",
                        },
                        {
                            columnName: "kode_brg",
                            columnTitle: "Kode Barang",
                            columnType: "data",
                        },
                        {
                            columnName: "nama_brg",
                            columnTitle: "Nama Barang",
                            columnType: "data",
                            titleColumn: true,
                        },
                        {
                            columnName: "qty",
                            columnTitle: "Jumlah",
                            columnType: "data",
                            textAlignment: "right",
                        },
                        {
                            columnName: "keterangan",
                            columnTitle: "Keterangan",
                            columnType: "data",
                            textAlignment: "left",
                        },
                    ]}
                    data={formData.list_estimasi_brg}
                    containerStyle={{
                        marginTop: "0.2rem",
                    }}
                />
            </FormContainer>
        {
            detailBrgToEdit ?
            <FormModal<EstimasiBrg>
                apiCaller={apiCaller}
                data={detailBrgToEdit}
                title={detailBrgToEdit.is_new ? "Tambah Detail Barang" : "Ubah Detail Barang"}
                fields={[
                    {
                        name: "brg",
                        keyField: "ucode",
                        labelField: "nama",
                        editKeyField: "ucode_brg",
                        title: "Barang",
                        inputType: "combobox",
                        dataType: (typeof Barang),
                        isReadonly: false,
                        isRequired: true,
                        searchEndpoint: "brg/nama",
                        searchEndpointFilterParamName: "nama",
                        ref: brgRef,
                        gridSizeSm: 6,
                        gridSizeMd: 6,
                        gridSizeLg: 6,
                    },
                    {
                        name: "qty",
                        title: "Jumlah",
                        inputType: "integer",
                        dataType: (typeof detailBrgToEdit.qty),
                        isReadonly: false,
                        isRequired: true,
                        gridSizeSm: 6,
                        gridSizeMd: 6,
                        gridSizeLg: 6,
                    },
                    {
                        name: "keterangan",
                        title: "Keterangan",
                        inputType: "textarea",
                        dataType: (typeof detailBrgToEdit.keterangan),
                        isReadonly: false,
                        isRequired: false,
                        rows: 3,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 12,
                    }
                ]}
                onClose={() => setDetailBrgToEdit(null)}
                onSave={(detailData?: EstimasiBrg) => {
                    onDetailChanged(detailData);
                    setDetailBrgToEdit(null);
                }}
                onValidate={(detailToCheck: EstimasiBrg, callback: Function) => {
                    onValidateDetailBrg(detailToCheck, callback);
                }}
            />
            :
            null
        }
        {
            validationResult?.length ?
            <NotificationDialog
                message={validationResult[0].message}
                onClose={() => setValidationResult(null)}
                title="Error!"
            />
            :
            null
        }
        </>
    );
};

export default Form;
